import { QueryClient, useQuery } from 'react-query'
import { toAbsoluteUrl } from '../../../../_metronic/helpers/AssetHelpers'
import { getSlugInfo } from './_requests'
import { EmpresaDtoPublica } from './_models'

export type LoginCompanyConfig = {
  id: number
  slug: string
  name: string
  image?: string
  logo?: string
}

type LoginCompanyConfigEntry = Omit<LoginCompanyConfig, 'slug' | 'image'> & {
  image?: string
  logo?: string
}

const LOGIN_COMPANY_CONFIG_QUERY_KEY = 'login-company-config'
export const LOGIN_COMPANY_CONFIG_STALE_TIME = 5 * 60 * 1000
const lauduzLoginImage = '/media/misc/auth-bg.png'
const lauduzLogo = '/media/logos/logo-white.png'

const loginCompanyConfigBySlug: Record<string, LoginCompanyConfigEntry> = {}

export const getEmpresaSlugFromLoginPath = (pathname: string): string | undefined => {
  const match = pathname.match(/^\/auth\/([^/]+)\/login\/?$/)

  return match?.[1]
}

export const getLoginCompanyConfigQueryKey = (empresaSlug?: string) => [
  LOGIN_COMPANY_CONFIG_QUERY_KEY,
  empresaSlug,
]

const mapEmpresaPublicaToLoginCompanyConfig = (empresa: EmpresaDtoPublica): LoginCompanyConfig => ({
  id: empresa.id,
  slug: empresa.slug,
  name: empresa.nome,
  image: empresa.imagem || lauduzLoginImage,
  logo: empresa.logo || lauduzLogo,
})

export const fetchLoginCompanyConfig = async (
  empresaSlug?: string
): Promise<LoginCompanyConfig | undefined> => {
  if (!empresaSlug) return undefined

  const cachedConfig = loginCompanyConfigBySlug[empresaSlug]
  if (cachedConfig) {
    return {
      ...cachedConfig,
      slug: empresaSlug,
    }
  }

  const response = await getSlugInfo(empresaSlug)

  const config = mapEmpresaPublicaToLoginCompanyConfig(response)
  loginCompanyConfigBySlug[config.slug] = {
    id: config.id,
    name: config.name,
    image: config.image,
    logo: config.logo,
  }

  return config
}

export const useLoginCompanyConfig = (empresaSlug?: string) => {
  const slug = empresaSlug

  return useQuery(getLoginCompanyConfigQueryKey(slug), () => fetchLoginCompanyConfig(slug), {
    enabled: !!slug,
    retry: false,
    staleTime: LOGIN_COMPANY_CONFIG_STALE_TIME,
  })
}

export const getLoginCompanyImageUrl = (image?: string): string => {
  const source = image || lauduzLoginImage

  if (/^(https?:)?\/\//.test(source) || /^(data|blob):/.test(source)) {
    return source
  }

  return toAbsoluteUrl(source.startsWith('/') ? source : `/${source}`)
}

export const preloadLoginCompanyImage = (image?: string): Promise<void> => {
  const imageUrl = getLoginCompanyImageUrl(image)

  if (/^data:/.test(imageUrl)) return Promise.resolve()

  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = imageUrl
  })
}

/**
 * Carrega a configuração da empresa (e a imagem lateral) antes de exibir o login por slug, para a
 * tela já abrir com a marca certa em vez de piscar a da Lauduz. Não lança: falha vira login
 * neutro/padrão, como sem pré-carga.
 */
export const prefetchLoginCompanyConfig = async (
  queryClient: QueryClient,
  empresaSlug?: string
): Promise<void> => {
  if (!empresaSlug) return

  try {
    const config = await queryClient.fetchQuery(
      getLoginCompanyConfigQueryKey(empresaSlug),
      () => fetchLoginCompanyConfig(empresaSlug),
      { staleTime: LOGIN_COMPANY_CONFIG_STALE_TIME }
    )

    await preloadLoginCompanyImage(config?.image)
  } catch {}
}

export const getCompanyLoginPath = (empresaSlug?: string): string =>
  empresaSlug ? `/auth/${empresaSlug}/login` : '/auth/login'

export const getCompanyLoginPathById = (empresaId?: string | number): string | undefined => {
  const id = empresaId ? Number(empresaId) : undefined
  if (!id) return undefined

  const companyEntry = Object.entries(loginCompanyConfigBySlug).find(
    ([, config]) => config.id === id
  )
  const slug = companyEntry?.[0]

  return slug ? getCompanyLoginPath(slug) : undefined
}
